import { createAction } from '@reduxjs/toolkit';

import type { DeactivatedFilters } from '../domain/deactivated-filters';

/**
 * "Signal" actions: intents the UI dispatches for the saga to act on. Kept out of
 * the slice's reducers: they trigger the paginated fetch / bulk restore (side
 * effects), not the state transitions (the saga owns the resulting `load*` /
 * `restore*` / cursor transitions).
 */

/** Refetch the current page (mount and after a settled restore). */
export const deactivatedRequested = createAction('deactivatedProfiles/deactivatedRequested');

/** Navigate to a 0-indexed page. */
export const pageChanged = createAction<number>('deactivatedProfiles/pageChanged');

/** Change the page size (resets to the first page). */
export const pageSizeChanged = createAction<number>('deactivatedProfiles/pageSizeChanged');

/**
 * Narrow the list (#536). One action for the whole toolbar rather than one per
 * control: the fetch takes them together, and a per-field action would make
 * "changed the method while a search was in flight" a case to reason about.
 *
 * The reducer applies it at once (the search box is controlled and must not
 * lag the keystroke) while the saga debounces only the REFETCH.
 */
export const filtersChanged = createAction<DeactivatedFilters>(
  'deactivatedProfiles/filtersChanged',
);

/**
 * Refetch the header aggregates. Its own intent, not a step of the page fetch:
 * the figures do not change with pagination.
 */
export const statsRequested = createAction('deactivatedProfiles/statsRequested');

/** Bulk-restore the given profile ids. */
export const restoreRequested = createAction<{ ids: string[] }>(
  'deactivatedProfiles/restoreRequested',
);

// ── Exclusion scan + blocklist (MR2) ──

/** Run the manual scan (deactivate every profile matching a keyword). */
export const scanRequested = createAction('deactivatedProfiles/scanRequested');

/** Load the preview count (dispatched when the scan-confirm dialog opens). */
export const scanPreviewRequested = createAction('deactivatedProfiles/scanPreviewRequested');

/** Load the blocklist (dispatched when the manage-keywords dialog opens). */
export const keywordsRequested = createAction('deactivatedProfiles/keywordsRequested');

/** Load the type names the Type filter offers (#702). */
export const profileTypesRequested = createAction('deactivatedProfiles/profileTypesRequested');

/** Add a keyword to the blocklist. */
export const keywordAddRequested = createAction<{ word: string }>(
  'deactivatedProfiles/keywordAddRequested',
);

/** Remove a keyword from the blocklist. */
export const keywordDeleteRequested = createAction<{ id: string }>(
  'deactivatedProfiles/keywordDeleteRequested',
);

// ── Reactivated tab (#512) ──

/** Fetch the Reactivated tab's current page (when the tab opens; after a restore). */
export const reactivatedRequested = createAction('deactivatedProfiles/reactivatedRequested');

/** Navigate the Reactivated tab to a 0-indexed page. */
export const reactivatedPageChanged = createAction<number>(
  'deactivatedProfiles/reactivatedPageChanged',
);

/** Change the Reactivated tab's page size (resets to the first page). */
export const reactivatedPageSizeChanged = createAction<number>(
  'deactivatedProfiles/reactivatedPageSizeChanged',
);
